import { useEffect, useState } from "react";
import { getResearchSource } from "../../Services/research";
import { evidenceLabel, safeText, sourceByline, sourceYear } from "./research";
import { RESEARCH_COPY } from "./copy";
import { showSuccessToast } from "../../Utils/toast";
import closeIcon from "../../Assets/icon/explore/close.svg";
import instituteIcon from "../../Assets/icon/explore/source-institute.svg";
import fileIcon from "../../Assets/icon/explore/source-file.svg";
import usersIcon from "../../Assets/icon/explore/finding-users.svg";
// No calendar glyph in the set yet: the date row reuses the document icon.
import dateIcon from "../../Assets/icon/explore/file.svg";
import checkIcon from "../../Assets/icon/explore/source-check.svg";
import infoIcon from "../../Assets/icon/explore/source-info.svg";
import shareIcon from "../../Assets/icon/explore/share.svg";
import copyIcon from "../../Assets/icon/explore/copy.svg";

/**
 * Research Source (Figma 10873:22543; WP-W8) — the sheet a source row opens.
 *
 * Filled from `GET /ARIS/v4/research/sources/{id}`. Library v26.18 (aris-ai
 * plan 45): "About this Source" shows Institution, Authors / Researchers,
 * Type of evidence and the typed source date, in that order, each hidden when
 * unknown. "Why Aris uses this source" is deleted; support and limitations
 * are the source's own text. No quote block (no approved quotes). View
 * Original Source and Copy Link appear only when `link_available`.
 */
function SourceSheet({ source, dialogueId, libraryVersion, onClose }) {
  const [detail, setDetail] = useState(null);
  const [failed, setFailed] = useState(false);
  const sourceId = source?.source_id;

  useEffect(() => {
    if (!sourceId) return undefined;
    let cancelled = false;
    setDetail(null);
    setFailed(false);
    const params = {};
    if (dialogueId) params.dialogue_id = dialogueId;
    if (libraryVersion) params.library_version = libraryVersion;
    getResearchSource(sourceId, params)
      .then((data) => !cancelled && setDetail(data || null))
      .catch((e) => {
        console.error("Failed to load the research source", e);
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [sourceId, dialogueId, libraryVersion]);

  useEffect(() => {
    if (!sourceId) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sourceId, onClose]);

  if (!sourceId) return null;

  const d = detail || {};
  const title = d.source_title || source.title;
  const institution = d.institution || source.institution;
  const authors = (d.authors?.length ? d.authors : source.authors || []).filter(safeText);
  const evidence = evidenceLabel(d.evidence_type || source.evidence_type);
  const year = sourceYear(source);
  const dated = safeText(d.published_reviewed) || safeText(source.date_label);
  const link = d.link_available && d.verified_url ? d.verified_url : null;

  const about = [
    institution && { icon: instituteIcon, label: RESEARCH_COPY.institution, value: institution },
    authors.length > 0 && {
      icon: usersIcon,
      label: RESEARCH_COPY.authors,
      value: authors.join(", "),
    },
    evidence && { icon: fileIcon, label: RESEARCH_COPY.evidenceType, value: evidence },
    // The label carries the date's kind: "Published 2019", "Updated 2024", "Reviewed 2025".
    dated && { icon: dateIcon, label: dated.split(" ")[0], value: dated.split(" ").slice(1).join(" ") },
  ].filter(Boolean);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      showSuccessToast?.(RESEARCH_COPY.copied);
    } catch (e) {
      console.error("Failed to copy the source link", e);
    }
  };

  return (
    <div className="ex-sheet" role="presentation" onClick={onClose}>
      <div
        className="ex-sheet__panel exr-source"
        role="dialog"
        aria-modal="true"
        aria-label={RESEARCH_COPY.sourceTitle}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="exr-source__close" onClick={onClose} aria-label="Close">
          <img src={closeIcon} alt="" width="20" height="20" />
        </button>

        <header className="exr-header">
          <h2 className="exr-header__title">{RESEARCH_COPY.sourceTitle}</h2>
          <p className="exr-header__sub">{RESEARCH_COPY.sourceSubtitle}</p>
        </header>

        <section className="exr-card">
          {sourceByline({ institution, authors }) && (
            <h3 className="exr-source__name">{sourceByline({ institution, authors })}</h3>
          )}
          {title && <p className="exr-source__meta">{title}</p>}
          {(evidence || year) && (
            <p className="exr-source__meta">{[evidence, year].filter(Boolean).join(" • ")}</p>
          )}
        </section>

        {failed && <p className="exr-source__meta">{RESEARCH_COPY.sourceError}</p>}

        {about.length > 0 && (
          <section className="exr-source__group">
            <h3 className="ex-section__title">{RESEARCH_COPY.aboutSource}</h3>
            <div className="ex-plist">
              {about.map((row) => (
                <div key={row.label} className="ex-plist__row exr-source__row">
                  <img src={row.icon} alt="" width="24" height="24" />
                  <span className="ex-plist__text">
                    <span className="ex-plist__title">{row.label}</span>
                    <span className="ex-plist__sub">{row.value}</span>
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {[
          { icon: checkIcon, label: RESEARCH_COPY.supports, value: safeText(d.what_this_supports) },
        ]
          .filter((row) => row.value)
          .map((row) => (
            <div key={row.label} className="ex-plist">
              <div className="ex-plist__row exr-source__row">
                <span className="ex-plist__tile ex-plist__tile--blue">
                  <img src={row.icon} alt="" width="24" height="24" />
                </span>
                <span className="ex-plist__text">
                  <span className="ex-plist__title">{row.label}</span>
                  <span className="ex-plist__sub">{row.value}</span>
                </span>
              </div>
            </div>
          ))}

        {safeText(d.important_limitations) && (
          <section className="exr-callout exr-callout--info">
            <img src={infoIcon} alt="" width="24" height="24" />
            <div>
              <h3 className="exr-callout__title">{RESEARCH_COPY.limitationsTitle}</h3>
              <p className="exr-callout__body">{d.important_limitations}</p>
            </div>
          </section>
        )}

        {link && (
          <div className="exr-source__actions">
            <a className="exd-cta__btn exr-source__open" href={link} target="_blank" rel="noopener noreferrer">
              {RESEARCH_COPY.viewOriginal}
              <img src={shareIcon} alt="" width="24" height="24" />
            </a>
            <button type="button" className="exr-source__copy" onClick={copyLink}>
              <img src={copyIcon} alt="" width="16" height="16" />
              {RESEARCH_COPY.copyLink}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SourceSheet;
